// Data hooks for /admin/releases.
//
// Each hook is the single seam between the UI and the data source. v1 returns
// mock data via React Query (so loading/error states are realistic and the swap
// to real APIs later is a one-file change per resource).
//
// Wiring strategy when backend phases land (see docs/plans/ADMIN_RELEASES.md):
//   - useApps      → static config (no API needed)
//   - useChannels  → static config (no API needed)
//   - useBuilds    → GET /api/admin/ota/builds       (Phase A)
//   - usePointers  → GET /api/admin/ota/pointers     (Phase A)
//   - useOtaAudit  → GET /api/admin/ota/audit        (Phase B; for now use auditLogs filter)
//   - useOtaDevices → already-real /api/admin/devices, joined with latest deviceUpdateLogs
//   - useUpdateLogStream → already-real /api/admin/neptune/updates

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import { signingKeyFromArtifactBucket } from './signingLabels';
import {
  OTA_APPS,
  OTA_CHANNELS,
  type AuditEntry,
  type Build,
  type ChannelPointer,
  type OtaApp,
  type OtaChannel,
  type OtaDevice,
  type OtaReleaseChannelId,
  type OtaSigningChannel,
  type OtaStructureStatus,
} from './types';

function normalizeBuild(raw: Build & { signingKey?: Build['signingKey'] }): Build {
  const bucket = raw.channel as OtaSigningChannel;
  return {
    ...raw,
    signingKey: raw.signingKey ?? signingKeyFromArtifactBucket(bucket),
    isValid: raw.isValid ?? true,
    error: raw.error ?? null,
  };
}
import {
  MOCK_AUDIT,
  MOCK_DEVICES,
  MOCK_POINTERS,
} from './mockData';

const STALE_MS = 30_000;

export function useApps() {
  return useQuery<readonly OtaApp[]>({
    queryKey: ['ota', 'apps'],
    queryFn: async () => OTA_APPS,
    staleTime: Infinity,
  });
}

export function useChannels() {
  return useQuery<readonly OtaChannel[]>({
    queryKey: ['ota', 'channels'],
    queryFn: async () => OTA_CHANNELS,
    staleTime: Infinity,
  });
}

export function useBuilds() {
  return useQuery<Build[]>({
    queryKey: ['ota', 'builds'],
    queryFn: async () => {
      const rows = (await api.adminGetOtaBuilds()) as Build[];
      return rows.map((b) => normalizeBuild(b));
    },
    staleTime: STALE_MS,
  });
}

export function useOtaStatus() {
  return useQuery<OtaStructureStatus>({
    queryKey: ['ota', 'status'],
    queryFn: async () => (await api.adminGetOtaStatus()) as OtaStructureStatus,
    staleTime: STALE_MS,
  });
}

export function usePointers() {
  // TODO(Phase A): GET /api/admin/ota/pointers
  return useQuery<ChannelPointer[]>({
    queryKey: ['ota', 'pointers'],
    queryFn: async () => MOCK_POINTERS,
    staleTime: STALE_MS,
  });
}

export function useOtaDevices() {
  // TODO: replace with /api/admin/devices joined with latest deviceUpdateLogs row.
  return useQuery<OtaDevice[]>({
    queryKey: ['ota', 'devices'],
    queryFn: async () => MOCK_DEVICES,
    staleTime: STALE_MS,
  });
}

export function useUpdateBuildReleaseNotes() {
  const qc = useQueryClient();
  return useMutation<
    Build,
    Error,
    { channel: string; appDir: string; versionDir: string; packageId: string; releaseNotes: string }
  >({
    mutationFn: async (params) =>
      (await api.adminUpdateOtaBuildReleaseNotes(params)) as Build,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ota', 'builds'] });
    },
  });
}

export function usePromoteBuild() {
  const qc = useQueryClient();
  return useMutation<
    Build,
    Error,
    { channel: string; appDir: string; versionDir: string; packageId: string }
  >({
    mutationFn: async (params) => (await api.adminPromoteOtaBuild(params)) as Build,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ota', 'builds'] });
    },
  });
}

/** Release-channel pointer update — mock until POST /api/admin/ota/promote (Phase B). */
export function usePromoteToReleaseChannel() {
  const qc = useQueryClient();
  return useMutation<
    { targetChannel: OtaReleaseChannelId; build: Build },
    Error,
    { targetChannel: OtaReleaseChannelId; build: Build }
  >({
    mutationFn: async (params) => {
      // TODO(Phase B): POST /api/admin/ota/promote { app, channel, versionCode, ... }
      await new Promise((r) => setTimeout(r, 300));
      return params;
    },
    onSuccess: ({ targetChannel, build }) => {
      const app = build.appDir as import('./types').OtaAppId;
      qc.setQueryData<ChannelPointer[]>(['ota', 'pointers'], (old) => {
        if (!old) return old;
        return old.map((p) => {
          if (p.app !== app || p.channel !== targetChannel) return p;
          return {
            ...p,
            previous: p.current,
            current: build.versionCode,
            promotedAt: new Date().toISOString(),
            promotedBy: 'admin',
            status: p.status === 'halted' ? 'halted' : 'healthy',
          };
        });
      });
      qc.invalidateQueries({ queryKey: ['ota', 'audit'] });
    },
  });
}

export function useOtaAudit() {
  // TODO(Phase B): GET /api/admin/ota/audit (or filter auditLogs by ota:* actions).
  return useQuery<AuditEntry[]>({
    queryKey: ['ota', 'audit'],
    queryFn: async () => MOCK_AUDIT,
    staleTime: STALE_MS,
  });
}
